import type { ColumnDef } from '@tanstack/react-table';
import { createAlphabeticalSortingFunction } from '@src/design-system/molecules/ticlick-table/table-cells/utils/sorting';
import { dayjsUtils } from '@shared/utils/helpers/dayjs';
import type { TAnnouncement } from '@src/shared/types/announcement';
import { AnnouncementMediaThumbnail } from '../components/announcement-media-thumbnail';
import { AnnouncementStatusBadge } from '../components/announcement-status-badge';
import { AnnouncementTypeBadge } from '../components/announcement-type-badge';
import { formatCompactCount } from '../utils/format-announcement-stats';

function formatTableDate(dateString?: string | null) {
  if (!dateString) return '-';
  return dayjsUtils(dateString).format('DD/MM/YYYY');
}

export const announcementsColumns = (): ColumnDef<TAnnouncement>[] => {
  return [
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Name',
      sortingFn: createAlphabeticalSortingFunction('name'),
      meta: { isCopy: true },
      cell: ({ row }) => {
        const { name, media } = row.original;

        return (
          <div className='flex items-center gap-2.5 min-w-0'>
            <AnnouncementMediaThumbnail
              file={media?.file}
              mimeType={media?.mime_type}
              alt={name}
            />
            <span className='truncate text-sm text-gray-900'>
              {name || '-'}
            </span>
          </div>
        );
      },
    },
    {
      id: 'type',
      accessorKey: 'media.mime_type',
      header: 'Type',
      cell: ({ row }) => (
        <AnnouncementTypeBadge mimeType={row.original.media?.mime_type} />
      ),
    },
    {
      id: 'views',
      accessorKey: 'views',
      header: 'Views',
      enableSorting: false,
      cell: ({ row }) => {
        const views = row.original.views_count;
        if (views == null) return <span>-</span>;
        return <span>{formatCompactCount(views)}</span>;
      },
    },
    {
      id: 'cta_label',
      accessorKey: 'cta_label',
      header: 'Button Name',
      sortingFn: createAlphabeticalSortingFunction('cta_label'),
      cell: ({ row }) => (
        <span className='truncate'>{row.original.cta_label || '-'}</span>
      ),
    },
    {
      id: 'cta_link',
      accessorKey: 'cta_link',
      header: 'Link',
      sortingFn: createAlphabeticalSortingFunction('cta_link'),
      meta: { isCopy: true },
      cell: ({ row }) => {
        const link = row.original.cta_link;
        if (!link) return <span>-</span>;

        return (
          <a
            href={link}
            target='_blank'
            rel='noopener noreferrer'
            className='truncate text-primary-1000 hover:underline'
            onClick={(event) => event.stopPropagation()}
          >
            {link}
          </a>
        );
      },
    },
    {
      id: 'clicks',
      accessorKey: 'clicks',
      header: 'Clicks',
      enableSorting: false,
      cell: ({ row }) => {
        const clicks = row.original.clicks_count;
        if (clicks == null) return <span>-</span>;
        return <span>{formatCompactCount(clicks)}</span>;
      },
    },
    {
      id: 'created_at',
      accessorKey: 'created_at',
      header: 'Created',
      cell: ({ row }) => (
        <span>
          {row.original.created_at
            ? formatTableDate(row.original.created_at)
            : '-'}
        </span>
      ),
    },
    {
      id: 'share_at',
      accessorKey: 'share_at',
      header: 'Share at',
      enableSorting: false,
      cell: ({ row }) => <span>{formatTableDate(row.original.share_at)}</span>,
    },
    {
      id: 'expire_at',
      accessorKey: 'expire_at',
      header: 'Expire at',
      enableSorting: false,
      cell: ({ row }) => <span>{formatTableDate(row.original.expire_at)}</span>,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <AnnouncementStatusBadge announcement={row.original} />
      ),
    },
  ];
};
